/**
 * Pages/Panel/PreNomina/SobreRecibo/ModalDesgloseFiscal.js
 *
 * "¿Cómo se calculó el ISR?" / "¿Cómo se calculó el IMSS?" del sobre-recibo.
 *
 * Es un renderizador GENÉRICO: no sabe nada de ISR ni de IMSS. Pinta lo que venga en
 * `desglose.secciones`, y cada sección puede traer `filas`, una `tabla` o `ramos`. Eso
 * hace que agregar una sección nueva sea trabajo de un solo lado (desglose.utils.js en
 * el backend) y que los dos desgloses —y su PDF y su Excel— salgan del mismo molde.
 *
 * El componente NO pide los datos: el sobre-recibo los trae una sola vez y se los pasa
 * ya cargados, porque un mismo recálculo produce el desglose del concepto, el del ISR y
 * el del IMSS.
 */

import React, { useState } from 'react'
import { Alert, Tag, Tooltip, message } from 'antd'
import { PrinterOutlined, FilePdfOutlined, FileExcelOutlined, CopyOutlined, DownOutlined, RightOutlined, ReloadOutlined } from '@ant-design/icons'
import { ModalItem, ButtonItem } from '../../../../Components/Items'
import { pdfDesglose, imprimirDesglose, excelDesglose, textoDesglose, fmtValor } from './desgloseExport'

// ─── Estilos (mismos que el resto del sobre-recibo: tabla HTML cruda) ────────
const TH = {
  border: '1px solid #d0d0d0', background: '#f5f5f5',
  padding: '5px 8px', fontSize: 11, fontWeight: 600,
  color: '#555', whiteSpace: 'nowrap', textAlign: 'center',
}
const TD     = { border: '1px solid #d0d0d0', padding: '4px 8px', fontSize: 12, verticalAlign: 'top' }
const TD_NUM = { ...TD, textAlign: 'right', fontFamily: 'monospace', whiteSpace: 'nowrap' }
const NOTA   = { fontSize: 11, color: '#888', fontStyle: 'italic', lineHeight: 1.45 }
const FORMULA = { fontSize: 10, color: '#8a8a8a', fontFamily: 'monospace' }

const esNumerico = (formato) => formato !== 'texto'

// ─── Encabezado plegable de sección ──────────────────────────────────────────
function Seccion({ seccion, children }) {
  const [abierta, setAbierta] = useState(!seccion.colapsada)
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        onClick={() => setAbierta(!abierta)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer',
          fontSize: 13, fontWeight: 600, color: '#1A3A5C',
          borderBottom: '2px solid #e8eef5', paddingBottom: 4, marginBottom: 8,
        }}
      >
        {abierta ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
        {seccion.titulo}
      </div>
      {abierta && (
        <>
          {seccion.nota && <div style={{ ...NOTA, marginBottom: 8 }}>{seccion.nota}</div>}
          {children}
        </>
      )}
    </div>
  )
}

// ─── Sección de filas etiqueta / cómo se obtiene / valor ─────────────────────
function Filas({ filas }) {
  if (!filas?.length) return null
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <tbody>
        {filas.map((f, i) => (
          <tr key={i} style={f.destacado ? { background: '#f6f9fc' } : undefined}>
            <td style={{ ...TD, width: '42%', fontWeight: f.destacado ? 600 : 400 }}>
              {f.etiqueta}
              {f.variable && (
                <Tooltip title="Nombre de la variable en las fórmulas de nómina">
                  <span style={{ ...FORMULA, marginLeft: 6 }}>{f.variable}</span>
                </Tooltip>
              )}
              {f.nota && <div style={{ ...NOTA, marginTop: 2 }}>{f.nota}</div>}
            </td>
            <td style={{ ...TD, ...FORMULA }}>{f.formula || ''}</td>
            <td style={{ ...TD_NUM, width: 130, fontWeight: f.destacado ? 700 : 400 }}>
              {fmtValor(f.valor, f.formato)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

// ─── Sección de tabla genérica ───────────────────────────────────────────────
function TablaGenerica({ tabla }) {
  if (!tabla) return null
  const { columnas, renglones, totales, vacia } = tabla
  if (!renglones?.length) return <div style={NOTA}>{vacia || 'Sin datos.'}</div>
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>{columnas.map(c => <th key={c.campo} style={TH}>{c.titulo}</th>)}</tr>
      </thead>
      <tbody>
        {renglones.map((r, i) => (
          <React.Fragment key={i}>
            <tr>
              {columnas.map(c => (
                <td key={c.campo} style={esNumerico(c.formato) ? TD_NUM : TD}>
                  {fmtValor(r[c.campo], c.formato)}
                  {c.campo === 'descripcion' && r.etiquetas?.map(e => (
                    <Tag key={e} color="blue" style={{ marginLeft: 4, fontSize: 10, padding: '0 4px' }}>{e}</Tag>
                  ))}
                </td>
              ))}
            </tr>
            {/* La fórmula que produjo el renglón va debajo, en chiquito: es la que
                explica un gravado que no debería estar gravado. */}
            {r.formula && (
              <tr>
                <td style={{ ...TD, borderTop: 'none', paddingTop: 0 }} />
                <td style={{ ...TD, ...FORMULA, borderTop: 'none', paddingTop: 0 }} colSpan={columnas.length - 1}>
                  {r.formula}
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
        {totales && (
          <tr style={{ background: '#f6f9fc', fontWeight: 700 }}>
            {columnas.map(c => (
              <td key={c.campo} style={esNumerico(c.formato) ? TD_NUM : TD}>
                {totales[c.campo] === undefined ? '' : fmtValor(totales[c.campo], c.formato)}
              </td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  )
}

// ─── Sección de ramos (IMSS) ─────────────────────────────────────────────────
function Ramos({ ramos, total }) {
  if (!ramos?.length) return null
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr>
          <th style={{ ...TH, textAlign: 'left' }}>Ramo</th>
          <th style={TH}>Base</th>
          <th style={TH}>Tasa</th>
          <th style={TH}>Días</th>
          <th style={TH}>Importe</th>
        </tr>
      </thead>
      <tbody>
        {ramos.map(r => {
          const tramos = r.anulado ? [] : r.tramos
          const filas = tramos.length || 1
          return (
            <React.Fragment key={r.clave}>
              {(tramos.length ? tramos : [null]).map((t, i) => (
                <tr key={i} style={r.cuadra ? undefined : { background: '#fff1f0' }}>
                  {i === 0 && (
                    <td style={{ ...TD, width: '46%' }} rowSpan={filas}>
                      <div style={{ fontWeight: 500 }}>{r.etiqueta}</div>
                      <div style={FORMULA}>{r.fundamento} · {r.tasaTexto}</div>
                      {r.notas?.map((n, k) => <div key={k} style={{ ...NOTA, marginTop: 2 }}>{n}</div>)}
                      {!r.cuadra && (
                        <div style={{ fontSize: 11, color: '#cf1322', fontWeight: 600, marginTop: 3 }}>
                          Este desglose no coincide con el importe que usó el sistema
                          ({fmtValor(r.esperado, 'moneda')}). El importe cobrado es el correcto;
                          lo que está mal es esta explicación.
                        </div>
                      )}
                    </td>
                  )}
                  {t ? (
                    <>
                      <td style={TD_NUM}>
                        {fmtValor(t.base, 'moneda')}
                        {tramos.length > 1 && <div style={FORMULA}>{t.etiqueta}</div>}
                      </td>
                      <td style={TD_NUM}>{fmtValor(t.tasa * 100, 'porcentaje')}</td>
                      <td style={TD_NUM}>{fmtValor(t.dias, 'dias')}</td>
                      <td style={TD_NUM}>{fmtValor(t.importe, 'moneda')}</td>
                    </>
                  ) : (
                    <td style={{ ...TD, textAlign: 'center', color: '#aaa' }} colSpan={4}>No aplica este periodo</td>
                  )}
                </tr>
              ))}
              {tramos.length > 1 && (
                <tr>
                  <td style={{ ...TD_NUM, fontWeight: 600 }} colSpan={4}>Total del ramo</td>
                  <td style={{ ...TD_NUM, fontWeight: 600 }}>{fmtValor(r.total, 'moneda')}</td>
                </tr>
              )}
            </React.Fragment>
          )
        })}
        {total && (
          <tr style={{ background: '#f6f9fc' }}>
            <td style={{ ...TD, fontWeight: 700 }} colSpan={4}>
              {total.etiqueta}
              {total.nota && <div style={NOTA}>{total.nota}</div>}
            </td>
            <td style={{ ...TD_NUM, fontWeight: 700 }}>{fmtValor(total.valor, 'moneda')}</td>
          </tr>
        )}
      </tbody>
    </table>
  )
}

// ─── Modal ───────────────────────────────────────────────────────────────────
export default function ModalDesgloseFiscal({
  open, onClose, tipo, desglose, loading, error, onReintentar,
  linea, prenomina, empresaNombre, avisoRecalculo, estatusLinea,
}) {
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(textoDesglose(desglose))
      message.success('Desglose copiado')
    } catch (e) {
      message.error('El navegador no permitió copiar al portapapeles')
    }
  }

  // El único modo de fallar es que el bloqueador de pop-ups se coma la ventana del
  // visor. pdfmake lanza un string pelón, así que aquí se traduce a algo accionable.
  const imprimir = () => {
    try {
      imprimirDesglose(desglose, linea, prenomina, { nombre: empresaNombre })
    } catch (e) {
      message.error(
        'El navegador bloqueó la ventana de impresión. Permite las ventanas emergentes '
        + 'de este sitio, o usa el botón de PDF e imprime el archivo.'
      )
    }
  }

  return (
    <ModalItem
      title={desglose?.titulo || (tipo === 'ISR' ? 'Cómo se calculó el ISR' : 'Cómo se calculó el IMSS')}
      visible={open}
      onCancel={onClose}
      footer={null}
      width={1000}
      paddingTop
      spinning={!!loading}
    >
      {error && (
        <Alert
          type="error" showIcon style={{ marginBottom: 12 }}
          message="No se pudo armar el desglose" description={error}
          action={<ButtonItem size="small" onClick={onReintentar} icon={<ReloadOutlined />}>Reintentar</ButtonItem>}
        />
      )}

      {!desglose && !loading && !error && (
        <div style={{ ...NOTA, padding: '24px 0', textAlign: 'center' }}>Sin datos que mostrar.</div>
      )}

      {desglose && (
        <>
          {/* Encabezado del empleado */}
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 18,
            background: '#f7f9fb', border: '1px solid #e8eef5', borderRadius: 6,
            padding: '8px 14px', marginBottom: 12, fontSize: 12,
          }}>
            {desglose.encabezado.map(e => (
              <div key={e.etiqueta}>
                <span style={{ color: '#888' }}>{e.etiqueta}: </span>
                <b>{typeof e.valor === 'number' ? fmtValor(e.valor, 'moneda') : e.valor}</b>
              </div>
            ))}
          </div>

          {/* El desglose se recalcula al abrirlo. Si algún importe salió distinto del que
              está guardado, hay que decirlo ANTES de que alguien cuadre contra un número
              que nunca estuvo en el recibo. */}
          {avisoRecalculo?.length > 0 && (
            <Alert
              type="warning" showIcon style={{ marginBottom: 12 }}
              message="Este desglose no corresponde exactamente al recibo guardado"
              description={
                <div style={{ fontSize: 12 }}>
                  <div style={{ marginBottom: 6 }}>
                    Para explicar los importes se vuelve a correr el cálculo con los catálogos de
                    HOY. {estatusLinea === 'timbrada' || estatusLinea === 'historica'
                      ? 'Esta línea ya está cerrada, así que lo que se le pagó al empleado es lo que está guardado.'
                      : 'Algo cambió desde la última vez que se calculó esta nómina.'}{' '}
                    Vuelve a calcular la nómina para que coincidan.
                  </div>
                  <table style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={TH}>Concepto</th>
                        <th style={TH}>En el recibo</th>
                        <th style={TH}>Recalculado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {avisoRecalculo.slice(0, 8).map(d => (
                        <tr key={d.conceptoNominaId}>
                          <td style={TD}>{d.serieNumero} {d.descripcion}</td>
                          <td style={TD_NUM}>{d.guardado === null ? '(no estaba)' : fmtValor(d.guardado, 'moneda')}</td>
                          <td style={TD_NUM}>{fmtValor(d.recalculado, 'moneda')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {avisoRecalculo.length > 8 && (
                    <div style={NOTA}>y {avisoRecalculo.length - 8} concepto(s) más.</div>
                  )}
                </div>
              }
            />
          )}

          {desglose.avisos?.map((a, i) => (
            <Alert key={i} type="warning" showIcon style={{ marginBottom: 8 }} message={a} />
          ))}

          <div style={{ marginTop: 14 }}>
            {desglose.secciones.map(sec => (
              <Seccion key={sec.clave} seccion={sec}>
                <Filas filas={sec.filas} />
                <TablaGenerica tabla={sec.tabla} />
                <Ramos ramos={sec.ramos} total={sec.total} />
              </Seccion>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #eee', paddingTop: 12 }}>
            <ButtonItem icon={<CopyOutlined />} onClick={copiar}>Copiar</ButtonItem>
            <ButtonItem
              icon={<FileExcelOutlined />}
              onClick={() => excelDesglose(desglose, linea, prenomina, { nombre: empresaNombre })}
            >
              Excel
            </ButtonItem>
            <ButtonItem
              icon={<FilePdfOutlined />}
              onClick={() => pdfDesglose(desglose, linea, prenomina, { nombre: empresaNombre })}
            >
              PDF
            </ButtonItem>
            <ButtonItem type="primary" icon={<PrinterOutlined />} onClick={imprimir}>
              Imprimir
            </ButtonItem>
          </div>
        </>
      )}
    </ModalItem>
  )
}
